/**
 * Customize mode: the approved content Sales may adjust.
 *
 * Executive summary, fit statements, scope, deliverables, approach and
 * engagement-specific assumptions — plus a read-only list of what remains
 * administrator-controlled, so it is clear what is *not* on offer here.
 *
 * Items are materialised from the approved template the first time this step
 * opens, so Sales never starts from a blank page and a Quick Proposal carries
 * no item state at all.
 */
import { useEffect } from 'react';
import type {
  ConfigurableItem,
  CustomAssumption,
  ProposalConfiguration,
  TemplateManifest,
} from '../../lib/api';
import { Alert } from '../../components/ui';
import { ItemEditor } from './ItemEditor';
import type { AIStatus } from '../../lib/api';
import { ExecutiveSummaryEditor } from './ExecutiveSummaryEditor';
import { AssumptionsEditor, LockedContentPanel } from './AssumptionsEditor';

/** Turn an approved set into STANDARD items. */
export function materialise(
  entries: Array<{ id: string; value: string; detail?: string; includedByDefault?: boolean }>,
): ConfigurableItem[] {
  return entries.map((entry) => ({
    id: entry.id,
    standardValue: entry.value,
    value: entry.value,
    ...(entry.detail !== undefined
      ? { standardDetail: entry.detail, detail: entry.detail }
      : {}),
    state: 'STANDARD' as const,
    included: entry.includedByDefault !== false,
  }));
}

/** The approved baseline for each section, used to materialise and to restore. */
export function baselineFor(manifest: TemplateManifest) {
  return {
    scope: manifest.scopeGroups.flatMap((group) =>
      group.items.map((item) => ({
        id: item.id,
        value: item.text,
        includedByDefault: item.includedByDefault,
      })),
    ),
    deliverables: manifest.deliverables.map((d) => ({
      id: d.id,
      value: d.name,
      detail: d.description,
      includedByDefault: d.includedByDefault,
    })),
    approachPhases: manifest.approachPhases.map((p) => ({
      id: p.id,
      value: p.name,
      detail: p.description,
    })),
    rightFit: (manifest.standardContent.rightFit ?? []).map((text, index) => ({
      id: `fit-${index + 1}`,
      value: text,
    })),
  };
}

export function ScopeStep({
  config,
  update,
  disabled,
  manifest,
  defaultExecutiveSummary,
  proposalId,
  aiStatus,
}: {
  config: ProposalConfiguration;
  update<K extends keyof ProposalConfiguration>(key: K, value: ProposalConfiguration[K]): void;
  disabled: boolean;
  manifest: TemplateManifest;
  defaultExecutiveSummary: string;
  proposalId: string;
  /** Null, or disabled, means no AI affordances appear anywhere in this step. */
  aiStatus: AIStatus | null;
}) {
  const baseline = baselineFor(manifest);

  // Materialise on first open, once per section. Until then the proposal
  // carries no item state and the engine renders the approved template.
  useEffect(() => {
    if (disabled) return;
    if (!config.scope) update('scope', materialise(baseline.scope));
    else if (!config.deliverables) update('deliverables', materialise(baseline.deliverables));
    else if (!config.approachPhases) {
      update('approachPhases', materialise(baseline.approachPhases));
    } else if (!config.rightFit) update('rightFit', materialise(baseline.rightFit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.scope,
    config.deliverables,
    config.approachPhases,
    config.rightFit,
    disabled,
    manifest.id,
  ]);

  const groupTitleFor = (itemId: string) =>
    manifest.scopeGroups.find((g) => g.items.some((i) => i.id === itemId))?.title;

  const scope = config.scope ?? [];
  const grouped = manifest.scopeGroups.map((group) => ({
    title: group.title,
    items: scope.filter((item) => groupTitleFor(item.id) === group.title),
  }));
  const customScope = scope.filter((item) => !groupTitleFor(item.id));

  return (
    <div className="space-y-6">
      <Alert tone="info">
        Everything here starts from the approved template. Anything you change is marked{' '}
        <strong>MODIFIED</strong>, anything you add is marked <strong>CUSTOM</strong>, and both
        are listed on the Review screen so it is clear how far this proposal departs from the
        approved baseline. <strong>Restore standard</strong> is available on every item and
        every section.
      </Alert>

      <ExecutiveSummaryEditor
        value={config.executiveSummary}
        approvedDefault={defaultExecutiveSummary}
        disabled={disabled}
        proposalId={proposalId}
        aiStatus={aiStatus}
        onChange={(next) => update('executiveSummary', next)}
        onRestore={() => update('executiveSummary', undefined)}
      />

      <ItemEditor
        title="Is this the right fit?"
        description="Approved qualifying statements. All included by default."
        items={config.rightFit ?? []}
        disabled={disabled}
        valueLabel="Statement"
        addLabel="Add statement"
        idPrefix="fit"
        onChange={(next) => update('rightFit', next)}
        onRestoreSection={() => update('rightFit', materialise(baseline.rightFit))}
      />

      {grouped.map((group) => (
        <ItemEditor
          key={group.title}
          title={`Scope — ${group.title}`}
          description="Approved scope items for this group."
          items={group.items}
          disabled={disabled}
          valueLabel="Scope item"
          addLabel="Add custom scope item"
          idPrefix="scope"
          proposalId={proposalId}
          aiStatus={aiStatus}
          aiFunctions={[
            'rewriteScopeItem',
            'makeMoreTechnical',
            'makeMoreConsultative',
            'proofreadProposal',
          ]}
          onChange={(next) => {
            const ids = new Set(group.items.map((i) => i.id));
            update('scope', [...scope.filter((i) => !ids.has(i.id)), ...next]);
          }}
          onRestoreSection={() => {
            const ids = new Set(group.items.map((i) => i.id));
            const restored = materialise(
              baseline.scope.filter((entry) => ids.has(entry.id)),
            );
            update('scope', [...scope.filter((i) => !ids.has(i.id)), ...restored]);
          }}
        />
      ))}

      {customScope.length > 0 && (
        <ItemEditor
          title={`Scope — ${config.customScopeGroupTitle || 'Additional Scope'}`}
          description="Items you added. They print in a group of their own so the approved grouping is not rewritten."
          items={customScope}
          disabled={disabled}
          valueLabel="Scope item"
          addLabel="Add custom scope item"
          idPrefix="scope"
          proposalId={proposalId}
          aiStatus={aiStatus}
          aiFunctions={[
            'rewriteScopeItem',
            'makeMoreTechnical',
            'makeMoreConsultative',
            'proofreadProposal',
          ]}
          onChange={(next) => {
            const ids = new Set(customScope.map((i) => i.id));
            update('scope', [...scope.filter((i) => !ids.has(i.id)), ...next]);
          }}
        />
      )}

      <ItemEditor
        title="Deliverables"
        description="Approved deliverables. Include, exclude, edit or add."
        items={config.deliverables ?? []}
        disabled={disabled}
        hasDetail
        valueLabel="Deliverable"
        detailLabel="Description"
        addLabel="Add custom deliverable"
        idPrefix="deliverable"
        onChange={(next) => update('deliverables', next)}
        onRestoreSection={() => update('deliverables', materialise(baseline.deliverables))}
      />

      <ItemEditor
        title="Our approach"
        description="Approved phases. Excluded phases are renumbered so the printed sequence has no gaps."
        items={config.approachPhases ?? []}
        disabled={disabled}
        hasDetail
        valueLabel="Phase name"
        detailLabel="What happens"
        addLabel="Add custom phase"
        idPrefix="phase"
        onChange={(next) => update('approachPhases', next)}
        onRestoreSection={() => update('approachPhases', materialise(baseline.approachPhases))}
      />

      <AssumptionsEditor
        approved={manifest.standardContent.assumptions ?? []}
        custom={config.customAssumptions ?? []}
        disabled={disabled}
        onChange={(next: CustomAssumption[]) => update('customAssumptions', next)}
      />

      <LockedContentPanel
        sections={[
          {
            id: 'why-lotus',
            title: 'Why Lotus Tech Solutions',
            reason: 'Approved differentiators. Changing them changes what the company claims.',
          },
          {
            id: 'corporate-metrics',
            title: 'Corporate performance claims',
            reason:
              'Years of experience, systems delivered, retention and support coverage are verified company figures.',
          },
          {
            id: 'confidentiality',
            title: 'Confidentiality and standard legal terms',
            reason: 'Contractual wording agreed with legal.',
          },
          {
            id: 'contact-details',
            title: 'Company contact details',
            reason: 'The company contact line printed on the cover page.',
          },
          {
            id: 'next-steps',
            title: 'Next steps wording',
            reason: 'The approved closing sequence.',
          },
        ]}
      />
    </div>
  );
}
